import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import { ApiResponse } from '@/apis/types';
import { OperationType } from '../common/enum';
import { clearAllStorageAndState } from '../common/helper/storageCleanup';
import { getTokenGeneration } from '../common/interceptor/sessionAbortController';

export type VendorConnectionStatus =
  'not_connected' | 'pending' | 'synced' | 'error';

/** One value a company enters to connect a vendor. Stored values come back as entered, secrets included, to
 * those who manage connections. Mirrors `CredentialFieldSerializer` in
 * shipment/serializer/vendor_connection_serializers.py. */
export interface CredentialFieldData {
  name: string;
  label: string;
  secret: boolean;
  optional: boolean;
  value: string | null;
  has_value: boolean;
}

/** A connectable Lane planner vendor and this company's connection to it. Mirrors `VendorConnectionSerializer`. */
export interface VendorConnectionData {
  vendor: string;
  display_name: string;
  description: string;
  /** Who first connected the vendor (name, and email for admins); null when not connected. */
  connected_by: { name: string; email: string | null } | null;
  connected: boolean;
  enabled: boolean;
  status: VendorConnectionStatus;
  status_detail: string;
  last_verified_at: string | null;
  updated_at: string | null;
  credential_fields: CredentialFieldData[];
}

export interface VendorConnectionList {
  vendors: VendorConnectionData[];
}

/** Body of a save: only what changed. An omitted credential keeps its stored value; an empty optional one clears it. */
export interface SaveVendorConnectionPayload {
  credentials?: Record<string, string>;
  enabled?: boolean;
}

/**
 * Rejects with the response for a call made without the shared error toasts
 * (the caller shows the failure). Those calls also skip the shared 401 logout,
 * so an expired session is handled here instead, except a 401 that belongs to
 * an earlier session (the interceptor ignores those too): a late answer must
 * not log out the session the user has just signed in to.
 */
const rejectSilently =
  (reject: (reason: unknown) => void) =>
  (error: {
    response?: { status?: number };
    config?: { _tokenGeneration?: number };
  }): void => {
    const requestGeneration = error?.config?._tokenGeneration;
    const stale =
      typeof requestGeneration === 'number' &&
      requestGeneration < getTokenGeneration();
    if (error?.response?.status === 401 && !stale) {
      // fire and forget: the rejection below is what the caller handles
      const _ = clearAllStorageAndState({
        broadcast: true,
        navigateToLogin: true,
      });
    }
    reject(error?.response);
  };

/** The interceptor resolves the axios response, whose body is the app's ApiResponse envelope. */
const asApiResponse = <T>(res: unknown): ApiResponse<T> =>
  res as ApiResponse<T>;

const vendorPath = (vendor: string): string =>
  `${APIPaths.laneVendorConnections}${encodeURIComponent(vendor)}/`;

/** GET the vendors this company can connect, with status and field metadata. */
export const GetVendorConnections = (
  token: string | null
): Promise<ApiResponse<VendorConnectionList>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.laneVendorConnections, token ?? undefined, null)
      .then((res) => resolve(asApiResponse<VendorConnectionList>(res)))
      .catch((error) => reject(error?.response));
  });

/** Connects a vendor or changes its credentials. Failures reject without the interceptor's toast, because the
 * caller shows them. */
export const SaveVendorConnection = (
  token: string | null,
  vendor: string,
  model: SaveVendorConnectionPayload
): Promise<ApiResponse<VendorConnectionData>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Put',
        vendorPath(vendor),
        model,
        token ?? undefined,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(asApiResponse<VendorConnectionData>(res)))
      .catch(rejectSilently(reject));
  });

/** Tests the stored credentials against the vendor ("Test connection"). Rejects without a toast. */
export const VerifyVendorConnection = (
  token: string | null,
  vendor: string
): Promise<ApiResponse<VendorConnectionData>> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${vendorPath(vendor)}verifications/`,
        undefined,
        token ?? undefined,
        undefined,
        OperationType.Write,
        true
      )
      .then((res) => resolve(asApiResponse<VendorConnectionData>(res)))
      .catch(rejectSilently(reject));
  });

/** Disconnects a vendor; the stored credentials are wiped server-side. */
export const DeleteVendorConnection = (
  token: string | null,
  vendor: string
): Promise<ApiResponse> =>
  new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', vendorPath(vendor), null, token ?? undefined)
      .then((res) => resolve(asApiResponse(res)))
      .catch((error) => reject(error?.response));
  });
